import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders } from './sharedStyles';

const POLL_INTERVAL = 5000;

export default function BusinessPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [business, setBusiness] = useState(null);

  const fetchData = useCallback(() => {
    fetch(`${API_BASE}/api/v1/monitor/business`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : null)
      .then(setBusiness)
      .catch((e) => console.error('Business fetch failed', e));
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  const byPlan = business?.tenantsByPlan || {};
  const planLabel = (key) => ({ SOLO: 'Solo', HIGH_STREET: 'High Street', CHAIN: 'Chain', INTERNAL: 'Internal' }[key] || key);

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.sections.business')}</h1>
        <div style={s.grid}>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.business.tenants')}</div>
            <div style={s.metricBig}>{business?.totalTenants ?? '\u2014'}</div>
            <div style={s.metricLabel}>{t('monitor.business.totalTenants')}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.business.shops')}</div>
            <div style={s.metricBig}>{business?.totalShops ?? '\u2014'}</div>
            <div style={s.metricLabel}>{t('monitor.business.totalShops')}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.business.employees')}</div>
            <div style={s.metricBig}>{business?.totalEmployees ?? '\u2014'}</div>
            <div style={s.metricLabel}>{t('monitor.business.totalUsers')}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.business.customersInQueue')}</div>
            <div style={s.metricBig}>{business?.b2cInQueue ?? '\u2014'}</div>
            <div style={s.metricLabel}>{t('monitor.business.currentlyWaiting')}</div>
          </div>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.business.activeQueues')}</div>
            <div style={s.metricBig}>{business?.activeQueues ?? '\u2014'}</div>
            <div style={s.metricLabel}>{t('monitor.business.queuesWithCustomers')}</div>
          </div>
        </div>

        {/* Tenant breakdown: trial/paid status + per-plan counts */}
        <div style={{ ...s.card, marginTop: '1.25rem' }}>
          <div style={s.cardTitle}>{t('monitor.business.tenantBreakdown')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
            <BreakdownTile label={t('monitor.business.trialing')} value={business?.tenantsTrialing} color="#06B6D4" />
            <BreakdownTile label={t('monitor.business.paid')} value={business?.tenantsPaid} color="#10B981" />
            <BreakdownTile label={t('monitor.business.trialExpired')} value={business?.tenantsTrialExpired} color="#EF4444" />
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #334155', paddingTop: '1rem' }}>
            <div style={{ ...s.cardTitle, marginBottom: '0.5rem' }}>{t('monitor.business.byPlan')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem' }}>
              {Object.keys(byPlan).length === 0 ? (
                <div style={{ color: '#64748B', fontSize: '0.85rem' }}>\u2014</div>
              ) : (
                Object.entries(byPlan).map(([plan, count]) => (
                  <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#0F172A', border: '1px solid #334155', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.85rem', color: '#CBD5E1', fontWeight: 600 }}>{planLabel(plan)}</span>
                    <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#F8FAFC' }}>{count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function BreakdownTile({ label, value, color }) {
  return (
    <div style={{ padding: '0.85rem 1rem', background: '#0F172A', border: '1px solid #334155', borderRadius: 10 }}>
      <div style={{ fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color, marginTop: 4 }}>{value ?? '\u2014'}</div>
    </div>
  );
}
